// src/components/FaxDetailPanel.jsx
// Slide-in side panel for one fax: inline document preview, details,
// failure reason, attempt history (outgoing) and actions.
import { reasonText, reasonWhy, incomingReasonText, incomingReasonWhy } from '../utils/faxReasons';
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { fetchFaxFile, fetchOutgoingDetail, cancelScheduledRetry, deleteFax, sendWaitingNow, fetchNotes, addFaxToNote } from '../utils/api';
import { STATUS_COLORS, formatFax, formatDate, formatRetryWindow, outStatus } from '../utils/format';
import { useNotify } from './Notifications';
import { isSuperAdmin } from '../utils/account';
import { API_BASE } from '../firebase/config';
import FaxProgress from './FaxProgress';
import Loader from './Loader';
import PdfPages, { needsPdfPages } from './PdfPages';
import Emo from './Emo';
import { usePhone } from '../hooks/useWindowWidth';
import { useClientWord } from '../hooks/useClientWord';
import { useContactNames, forgetContactName, knownContact } from '../hooks/useContactNames';
import { useSendTimings } from '../hooks/useSendTimings';

const RETRY_CANCEL_REASONS = {
  'file-expired'     : 'the stored file had expired',
  'destination-guard': 'this number had too many failed faxes in the last 24 hours',
  'admin'            : 'an administrator cancelled it',
  'user'             : 'the sender cancelled it',
};

export default function FaxDetailPanel({
  type, row, onClose, onPrev, onNext, hasPrev, hasNext, onResend, onRetryCancelled, onDeleted, onUpdate, inNotes = false,
}) {
  const atCo = useClientWord() === 'Client' ? 'the client' : 'your company';   // 3.67.5: the people at a client read 'your company'
  const { getToken, can, shows, effectiveInfo, userInfo, viewAs } = useAuth();
  const tm = useSendTimings();   // 3.75.0
  // 3.68.7 (owner): the number's name under the headline; clicking the
  // number names it for the company (as Contacts → Edit)
  const otherField = type === 'outgoing' ? 'toNumber' : 'fromNumber';
  const nameOf = useContactNames([row], [otherField]);
  const [naming, setNaming] = useState(false);
  // 3.51.0: phones: full screen, tighter, and a swipe left or right opens the
  // next or previous fax (the preview itself scrolls and zooms as usual)
  const phone = usePhone();
  const swipe = useRef(null);
  const onTouchStart = e => { const t = e.touches[0]; swipe.current = t && e.touches.length === 1 ? { x: t.clientX, y: t.clientY, at: Date.now() } : null; };
  const onTouchEnd = e => {
    const st = swipe.current; swipe.current = null;
    const t = e.changedTouches[0];
    if (!st || !t || Date.now() - st.at > 700) return;
    const dx = t.clientX - st.x, dy = t.clientY - st.y;
    if (Math.abs(dx) < 70 || Math.abs(dx) < Math.abs(dy) * 2) return;
    if (dx < 0 && hasNext) onNext(); else if (dx > 0 && hasPrev) onPrev();
  };
  // Opening a received fax makes it seen for the client (3.34.0), except for
  // Super Admins and View as (support), which the backend doesn't count
  const openingCounts = !isSuperAdmin(userInfo) && !viewAs;
  const markRef = useRef({});
  markRef.current = { isOutgoing: type === 'outgoing', seen: row.seen, openingCounts, onUpdate };
  const canFiles = can('fax.files');
  const { notify, confirm } = useNotify();
  const isOutgoing   = type === 'outgoing';
  const noDocument   = !isOutgoing && row.failedIncoming === true;

  // Which attempt is shown (outgoing faxes can have several)
  const [faxId, setFaxId]       = useState(row.faxId);
  const [detail, setDetail]     = useState(null);
  const [detailError, setDetailError] = useState('');
  const [detailTick, setDetailTick]   = useState(0);
  const [preview, setPreview]   = useState({ state: 'loading' });
  const [copied, setCopied]     = useState(false);
  const [resending, setResending] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [sendingNow, setSendingNow] = useState(false);   // 3.69.4
  const [actionError, setActionError] = useState('');
  const [deleting, setDeleting] = useState(false);

  const closeRef   = useRef(null);
  const returnRef  = useRef(null);

  // New row (prev/next or a fresh open) — start over on that fax
  useEffect(() => {
    setFaxId(row.faxId);
    setDetail(null);
    setDetailError('');
    setCopied(false);
  }, [row.faxId]);

  // Attempt history for outgoing faxes
  useEffect(() => {
    if (!isOutgoing) return;
    let cancelled = false;
    setDetailError('');
    fetchOutgoingDetail(row.faxId, getToken)
      .then(d => { if (!cancelled) setDetail(d); })
      .catch(e => { if (!cancelled) setDetailError(e.message); });
    return () => { cancelled = true; };
  // Refetch when the row's status changes (live updates in the list)
  }, [isOutgoing, row.faxId, row.status, row.requeued, detailTick, getToken]);

  // Document preview. Every attempt of an outgoing fax sends the same
  // stored file, so the preview follows the opened row, not the attempt.
  useEffect(() => {
    if (noDocument) { setPreview({ state: 'none' }); return; }
    if (!canFiles) { setPreview({ state: 'error', error: "Your role can't open fax files." }); return; }
    let cancelled = false;
    let url       = null;
    setPreview({ state: 'loading' });
    fetchFaxFile(type, row.faxId, getToken, { inline: true })
      .then(blob => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setPreview({ state: 'ready', url, mime: blob.type || '', blob });   // 3.54.2: the file itself, for PdfPages
        const m = markRef.current;
        if (!m.isOutgoing && m.seen === false && m.openingCounts) m.onUpdate?.(row.faxId, { seen: true });
      })
      .catch(e => { if (!cancelled) setPreview({ state: 'error', error: e.message }); });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [type, row.faxId, noDocument, getToken, canFiles]);

  // Focus the panel on open and give focus back on close
  useEffect(() => {
    returnRef.current = document.activeElement;
    closeRef.current?.focus();
    return () => { returnRef.current?.focus?.(); };
  }, []);

  // Esc closes; arrow keys step through the list (when not typing)
  useEffect(() => {
    function onKey(e) {
      const tag = (e.target.tagName || '').toLowerCase();
      if (['input', 'textarea', 'select'].includes(tag)) return;
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
      else if (e.key === 'ArrowDown' && hasNext) { e.preventDefault(); onNext(); }
      else if (e.key === 'ArrowUp'   && hasPrev) { e.preventDefault(); onPrev(); }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, onNext, onPrev, hasNext, hasPrev]);

  const chain   = detail?.chain || [];
  const fax     = chain.find(a => a.faxId === faxId) || row;
  const status  = isOutgoing
    ? outStatus(fax)
    : (fax.status || 'received');
  const latest  = chain[chain.length - 1];
  // Resend only the latest failed attempt, and only when no automatic
  // retry is already scheduled. Wait for the history before offering it;
  // if the history can't load, fall back to the row's own status.
  const canResend = isOutgoing && shows('fax.resend') && fax.status === 'failed' && !fax.requeued
    && (detail ? (latest?.faxId === fax.faxId && !detail.scheduledRetry) : !!detailError);
  // A scheduled automatic retry can be cancelled from the latest attempt
  const canCancelRetry = isOutgoing && shows('fax.cancelRetry') && !!detail?.scheduledRetry
    && latest?.faxId === fax.faxId && fax.status === 'failed' && !fax.requeued;
  const fileName = fax.fileName || `fax-${fax.faxId}`;

  async function copyId() {
    try {
      await navigator.clipboard.writeText(fax.faxId);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked — the ID is still selectable */ }
  }

  async function download() {
    if (preview.state === 'ready') {
      const a = document.createElement('a');
      a.href = preview.url;
      a.download = fileName;
      a.click();
      return;
    }
    try {
      const blob = await fetchFaxFile(type, row.faxId, getToken);
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      notify({ tone: 'error', title: 'Download failed', detail: e.message });
    }
  }

  async function cancelRetry() {
    const ok = await confirm({
      title: 'Cancel the automatic retry?',
      message: `It is scheduled for around ${formatRetryWindow(detail.scheduledRetry.fireAt)}. The fax won't be sent again unless you resend it.`,
      confirmLabel: 'Cancel retry',
      cancelLabel: 'Keep retry',
      danger: true,
    });
    if (!ok) return;
    setCancelling(true);
    setActionError('');
    try {
      await cancelScheduledRetry(fax.faxId, getToken);
      onRetryCancelled?.(fax.faxId);
      notify({ bell: 'fax', tone: 'success', title: 'Automatic retry cancelled', detail: `To ${formatFax(fax.toNumber)}` });
    } catch (e) {
      setActionError(e.message);
    }
    setCancelling(false);
    setDetailTick(t => t + 1);
  }

  // 3.69.4 (owner): a Resend waiting for the line goes out now
  async function sendNow() {
    const w = detail?.waitingLine;
    if (!w) return;
    const ok = await confirm({
      title: 'Send this fax now?',
      message: `It goes to ${formatFax(fax.toNumber)} now instead of waiting in line${w.slow ? `. This number is hard to reach, so faxes to it are normally spaced ${tm.hardSpacing} apart` : ''}. If a fax to that number is on the line right now, try again in a few minutes.`,
      confirmLabel: 'Send now',
    });
    if (!ok) return;
    setSendingNow(true);
    setActionError('');
    try {
      await sendWaitingNow(w.queueId, getToken);
      notify({ bell: 'fax', tone: 'success', title: 'Fax sent', detail: `To ${formatFax(fax.toNumber)}` });
    } catch (e) {
      setActionError(e.message);
    }
    setSendingNow(false);
    setDetailTick(t => t + 1);
  }

  // Mark as new / seen (3.34.0)
  const [marking, setMarking] = useState(false);
  async function toggleSeen() {
    setMarking(true); setActionError('');
    try {
      const seen = row.seen === false;
      const r = await fetch(`${API_BASE}/portal/incoming/${encodeURIComponent(row.faxId)}/seen`, { method: 'POST', headers: { Authorization: `Bearer ${await getToken()}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ seen }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Couldn't change it.");
      onUpdate?.(row.faxId, { seen });
    } catch (e) { setActionError(e.message); }
    setMarking(false);
  }

  // Delete for the whole group (soft delete: kept for logs, reports and
  // billing; listed in Trash)
  async function removeFax() {
    const list  = isOutgoing ? 'Outbox' : 'Inbox';
    const group = effectiveInfo?.groupLabel && effectiveInfo.groupLabel !== 'All groups' ? effectiveInfo.groupLabel : 'your company';
    const ok = await confirm({
      title: 'Delete this fax?',
      message: `It will be removed from the ${list} for everyone in ${group}${isOutgoing && chain.length > 1 ? ', together with its other attempts' : ''}.\n\nA record is still kept for the logs, reports and Usage, and it will be listed in Trash.`,
      confirmLabel: 'Delete fax',
      danger: true,
    });
    if (!ok) return;
    setDeleting(true);
    setActionError('');
    try {
      const r = await deleteFax(type, row.faxId, getToken);
      notify({ bell: 'fax', tone: 'success', title: 'Fax deleted', detail: 'It\'s now in Trash.' });
      onDeleted?.(r.deleted || [row.faxId]);
    } catch (e) {
      setActionError(e.message);
      setDeleting(false);
    }
  }

  async function resend() {
    setResending(true);
    const newId = await onResend(fax.faxId);
    setResending(false);
    if (newId) setDetailTick(t => t + 1);
  }

  const direction = isOutgoing ? 'Sent fax' : 'Received fax';
  const otherNumber = isOutgoing ? fax.toNumber : fax.fromNumber;
  const shownName = nameOf(row.accountId, otherNumber);
  const canName = shows('contacts.edit') && !!row.accountId && /^\d{10}$/.test(String(otherNumber || '').replace(/\D/g, '').slice(-10));
  const headline = (
    <>
      {isOutgoing ? 'To ' : 'From '}
      {canName
        ? <button type="button" onClick={() => setNaming(true)} style={S.numberBtn} className="fax-name-btn"
            title={shownName && shownName !== 'No name' ? 'Change the name for this number' : 'Give this number a name'}>{formatFax(otherNumber)} <span style={S.pencil} aria-hidden="true">✎</span></button>
        : formatFax(otherNumber)}
    </>
  );

  return (
    <div data-fax-panel style={S.overlay} onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <aside style={phone ? { ...S.panel, width: '100vw', boxShadow: 'none' } : S.panel} role="dialog" aria-modal="true" aria-labelledby="fax-detail-title"
        onTouchStart={phone ? onTouchStart : undefined} onTouchEnd={phone ? onTouchEnd : undefined}>
        {/* Header */}
        <div style={phone ? { ...S.header, padding: '12px 14px' } : S.header} className="dlg-head">
          <div style={{ minWidth: 0 }}>
            <div style={S.eyebrow}>{direction}</div>
            <h2 id="fax-detail-title" style={phone ? { ...S.title, fontSize: 17 } : S.title}>{headline}</h2>
            {shownName && shownName !== 'No name'
              ? <div style={S.numberName} title={shownName}>{shownName}</div>
              : canName && shownName === 'No name' && <button type="button" onClick={() => setNaming(true)} style={S.addName} className="fax-name-btn">+ Add a name</button>}
            <div style={{ marginTop: 6 }}>
              {status === 'queued' ? <FaxProgress fax={{ ...fax, ...rowLive(row, fax) }} /> : <Badge status={status} />}
            </div>
          </div>
          <div style={S.headerBtns}>
            <button onClick={onPrev} disabled={!hasPrev} style={phone ? { ...S.navBtn, width: 40, height: 40 } : S.navBtn}
              title="Previous fax (↑)" aria-label="Previous fax">‹</button>
            <button onClick={onNext} disabled={!hasNext} style={phone ? { ...S.navBtn, width: 40, height: 40 } : S.navBtn}
              title="Next fax (↓)" aria-label="Next fax">›</button>
            <button ref={closeRef} onClick={onClose} style={phone ? { ...S.closeBtn, width: 40, height: 40 } : S.closeBtn}
              title="Close (Esc)" aria-label="Close">✕</button>
          </div>
        </div>

        <div style={phone ? { ...S.body, padding: 14, gap: 14 } : S.body}>
          {/* Failure reason — shown plainly, not behind a hover */}
          {status === 'failed' && fax.failureReason && (
            <div style={S.failBox}>
              <div style={S.failLabel}>Failure reason</div>
              {/* 3.57.7: in plain words, with why / what to do (as in the emails) */}
              {/* 3.62.23: a received fax that failed is worded from our side */}
              <strong>{isOutgoing ? reasonText(fax.failureReason, fax.failureContext) : incomingReasonText(fax.failureReason)}.</strong>
              {(isOutgoing ? reasonWhy(fax.failureReason, fax.failureContext) : incomingReasonWhy(fax.failureReason)) && <div style={{ marginTop: 6, fontWeight: 400 }}>{isOutgoing ? reasonWhy(fax.failureReason, fax.failureContext) : incomingReasonWhy(fax.failureReason)}</div>}
            </div>
          )}

          {/* Preview */}
          {/* 3.60.0 (owner): with a failure reason above it, the preview is 75% as tall, so the details below come into view sooner */}
          <Preview preview={preview} fileName={fileName} onDownload={download} short={status === 'failed' && !!fax.failureReason} />

          {/* Details */}
          <dl style={S.details}>
            {isOutgoing ? (
              <>
                <Field label="Sent by" value={fax.fromEmail} />
                <Field label="Sent to" value={formatFax(fax.toNumber)} mono />
                <Field label="Sent at" value={formatDate(fax.sentAt)} />
                <Field label="Delivered at" value={formatDate(fax.deliveredAt)} />
              </>
            ) : (
              <>
                <Field label="From" value={formatFax(fax.fromNumber)} mono />
                <Field label="To" value={formatFax(fax.toNumber)} mono />
                <Field label={noDocument ? 'Failed at' : 'Received at'} value={formatDate(fax.receivedAt)} />
                {/* 3.34.0: who at the client opened it (Roles & Access: inbox.seenBy) */}
                {/* 3.37.4: who the fax was emailed to (and if the email didn't go) */}
                {row.emailed && (
                  <Field label="Emailed to" value={row.emailed.ok
                    ? `${(row.emailed.to || []).join(', ')}${row.emailed.cc?.length ? ` · Cc ${row.emailed.cc.join(', ')}` : ''}${row.emailed.bccCount ? ` · Bcc ${row.emailed.bccCount}` : ''}`
                    : `Not emailed: ${row.emailed.reason === 'no recipients' ? 'nobody is set to get this line’s faxes' : 'the email failed'}`} />
                )}
                {/* 3.49.23: New is per person; Seen by lists everyone who opened it */}
                {/* 3.54.0 (owner): only for roles that may see who opened it (inbox.seenBy); the others never get the list */}
                {!noDocument && shows('inbox.seenBy') && (row.seen === false || row.seenBy || row.seenVia === 'email') && (
                  <Field label="Seen by" value={[(row.seenBy || []).map(p => `${p.name || p.email} (${formatDate(p.at)})`).join(' · '), row.seenVia === 'email' && 'Delivered by email'].filter(Boolean).join(' · ')
                    || (row.seen === false ? 'Nobody yet (new)' : '—')} />
                )}
              </>
            )}
            <Field label="File name" value={fileName} />
            <Field label="Pages" value={fax.pages || '—'} />
            <div style={S.field}>
              <dt style={S.dt}>Reference ID</dt>
              <dd style={{ ...S.dd, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={S.refId}>{fax.faxId}</span>
                <button onClick={copyId} style={S.copyBtn}>{copied ? 'Copied' : 'Copy'}</button>
              </dd>
            </div>
          </dl>

          {/* Attempt history */}
          {isOutgoing && (
            <section>
              <h3 style={S.sectionTitle}>History</h3>
              {detailError ? (
                <div style={S.muted}>Could not load history: {detailError}</div>
              ) : !detail ? (
                <Loader size="sm" label="Loading history…" />
              ) : (
                <Timeline chain={chain} current={faxId} onSelect={setFaxId}
                  scheduledRetry={detail.scheduledRetry} waitingLine={detail.waitingLine} />
              )}
            </section>
          )}
        </div>

        {/* Actions */}
        <div style={phone ? { ...S.footer, padding: '10px 14px calc(10px + env(safe-area-inset-bottom, 0px))', gap: 8 } : S.footer}>
          {!noDocument && canFiles && (
            <button onClick={download} disabled={preview.state === 'error'}
              style={preview.state === 'error' ? { ...S.primaryBtn, ...S.btnDisabled } : S.primaryBtn}
              title={preview.state === 'error' ? 'The file is not available' : 'Download this fax'}>
              <Emo e="⬇" /> Download
            </button>
          )}
          {noDocument && <span style={S.muted}>Nothing to download for a failed incoming fax.</span>}
          {preview.state === 'ready' && (preview.mime === 'application/pdf' || preview.mime.startsWith('image/')) && (
            <button onClick={() => window.open(preview.url, '_blank', 'noopener')} style={S.secondaryBtn}>
              Open in new tab
            </button>
          )}
          {!isOutgoing && !noDocument && row.seen !== undefined && can('inbox.markNew') && !viewAs && (
            <button onClick={toggleSeen} disabled={marking} style={S.secondaryBtn}
              title={!openingCounts ? (row.seen === false ? `Mark it as seen for everyone at ${atCo}` : `Show it as new again for everyone at ${atCo}`)
                : (row.seen === false ? 'Mark it as seen for you' : 'Show it as new again for you')}>
              {marking ? 'Saving…' : row.seen === false ? '✓ Mark as seen' : '● Mark as new'}
            </button>
          )}
          {canResend && (
            <button onClick={resend} disabled={resending} style={S.resendBtn}>
              {resending ? 'Resending…' : <><Emo e="🔄" /> Resend</>}
            </button>
          )}
          {/* 3.49.22: send it now instead of waiting for the automatic retry
              (the retry is cancelled; the line's pacing still applies) */}
          {canCancelRetry && onResend && shows('fax.resend') && (
            <button onClick={resend} disabled={resending} style={S.resendBtn}
              title={`Send it now instead of waiting for the automatic retry around ${formatRetryWindow(detail.scheduledRetry.fireAt)}`}>
              {resending ? 'Resending…' : <><Emo e="🔄" /> Resend now</>}
            </button>
          )}
          {/* 3.69.4 (owner): a Resend waiting for the line, sent now */}
          {isOutgoing && detail?.waitingLine?.canSendNow && !viewAs && (
            <button onClick={sendNow} disabled={sendingNow} style={S.resendBtn} title="Send it now instead of waiting in line">
              {sendingNow ? 'Sending…' : <><Emo e="🔄" /> Send now</>}
            </button>
          )}
          {canCancelRetry && (
            <button onClick={cancelRetry} disabled={cancelling} style={onResend && shows('fax.resend') ? { ...S.cancelRetryBtn, marginLeft: 0 } : S.cancelRetryBtn}
              title={`Stop the automatic retry scheduled for around ${formatRetryWindow(detail.scheduledRetry.fireAt)}`}>
              {cancelling ? 'Cancelling…' : '✕ Cancel retry'}
            </button>
          )}
          {/* 3.76.0 (owner): add this fax to one of your notes (yours only: not in View as, nor inside My notes) */}
          {!viewAs && !inNotes && <AddToNotes fax={fax} isOutgoing={isOutgoing} status={status} otherNumber={otherNumber} getToken={getToken} notify={notify} />}
          {shows('fax.delete') && onDeleted && (
            <button onClick={removeFax} disabled={deleting} style={S.deleteBtn} title="Remove this fax from the list for everyone in the group">
              {deleting ? 'Deleting…' : <><Emo e="🗑" /> Delete</>}
            </button>
          )}
          {actionError && <div style={S.actionError} role="alert">{actionError}</div>}
        </div>
      </aside>
      {naming && <NameDialog accountId={row.accountId} number={otherNumber} superView={atCo === 'the client'}
        clientName={(effectiveInfo?.accounts || []).find(a => a.id === row.accountId)?.name || row.accountName || 'the client'} onClose={() => setNaming(false)} />}
    </div>
  );
}

// 3.76.0 (owner, Nov 1): 📝 Add to my notes: a new note, or one of yours.
// The note keeps the fax's number and date; opening it later checks you can
// still see the fax.
function AddToNotes({ fax, isOutgoing, status, otherNumber, getToken, notify }) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(null);
  const [busy, setBusy] = useState(false);
  const box = useRef(null);
  useEffect(() => {   // a click outside closes the menu
    if (!open) return undefined;
    const onDown = e => { if (!box.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);
  useEffect(() => {
    if (!open || notes) return;
    fetchNotes(getToken).then(j => setNotes((j.data || []).filter(n => !n.access || n.access === 'owner'))).catch(() => setNotes([]));   // 3.77.0: your own notes only
  }, [open, notes, getToken]);
  const title = n => n.title?.trim() || (n.kind === 'list' ? n.items?.find(i => i.text?.trim())?.text : String(n.text || '').split('\n').find(l => l.trim())) || 'Untitled note';
  async function add(id) {
    setBusy(true);
    try {
      const link = { type: isOutgoing ? 'outgoing' : 'incoming', faxId: fax.faxId, number: otherNumber || '', at: (isOutgoing ? fax.sentAt : fax.receivedAt) || null, failed: status === 'failed' };
      const { note } = await addFaxToNote(id, link, getToken);
      setOpen(false); setNotes(null);
      notify({ tone: 'success', icon: '📝', title: id === 'new' ? 'Added to a new note' : 'Added to your note', detail: `“${title(note)}” in My notes.`, hint: 'Open it',
        onClick: () => window.dispatchEvent(new CustomEvent('open-notes', { detail: { noteId: note.id } })) });
    } catch (e) { notify({ tone: 'error', title: 'Couldn’t add it to your notes', detail: e.message }); }
    setBusy(false);
  }
  const item = { display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', borderRadius: 6, padding: '7px 9px', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, color: 'var(--t-263238)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
  return (
    <span ref={box} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(o => !o)} aria-expanded={open} disabled={busy} style={S.secondaryBtn} title="Keep this fax in one of your notes (only you see your notes)"><Emo e="📝" /> Add to my notes</button>
      {open && <div role="menu" aria-label="Add to my notes" onKeyDown={e => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } }}
        style={{ position: 'absolute', bottom: '100%', left: 0, marginBottom: 6, zIndex: 5, width: 260, maxHeight: 300, overflowY: 'auto', background: 'var(--c-ffffff)', border: '1px solid var(--c-e0e0e0)', borderRadius: 10, boxShadow: '0 10px 30px rgb(var(--c-000000-rgb) / 0.18)', padding: 6 }}>
        <button role="menuitem" onClick={() => add('new')} disabled={busy} style={{ ...item, fontWeight: 700, color: 'var(--t-00796b)' }}>＋ New note</button>
        {!notes ? <div style={{ padding: 8 }}><Loader size="sm" /></div>
          : notes.map(n => <button key={n.id} role="menuitem" onClick={() => add(n.id)} disabled={busy} style={item}>{n.kind === 'list' ? '☑ ' : ''}{title(n)}</button>)}
      </div>}
    </span>
  );
}

// 3.68.7 (owner): name the fax's number for the company, from the panel.
// The same save as Contacts → Edit (POST /portal/contacts, contacts.edit):
// only this company's name; empty = back to the phone listing.
// 3.68.8 (owner-approved wording): what an empty box goes back to, said
// by what the number shows now: a place, a phone network label or a name
function emptyLine(shown) {
  if (!shown) return '';
  if (shown.startsWith('📍')) return `Leave it empty to show where the number is from (${shown}).`;
  return `Leave it empty to show "${shown}" again.`;
}
function NameDialog({ accountId, number, superView, clientName, onClose }) {
  const { getToken } = useAuth();
  const { notify } = useNotify();
  const k = knownContact(accountId, number);
  const [name, setName] = useState(k?.name || '');
  const [busy, setBusy] = useState(false);
  // Esc closes this first, not the fax
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') { e.preventDefault(); e.stopImmediatePropagation(); onClose(); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);
  async function save() {
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/portal/contacts`, { method: 'POST', headers: { Authorization: `Bearer ${await getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ accountId, number, name, from: 'fax' }) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Couldn't save the name.");
      forgetContactName(accountId, number);
      notify({ tone: 'success', title: name.trim() ? 'Name saved' : 'Name removed', detail: formatFax(number) });
      onClose();
    } catch (e) { notify({ tone: 'error', title: "Couldn't save the name", detail: e.message }); setBusy(false); }
  }
  return (
    <div style={S.nameOverlay} onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={S.nameCard} role="dialog" aria-modal="true" aria-labelledby="fax-name-title">
        <h3 id="fax-name-title" style={S.nameTitle}>Name for {formatFax(number)}</h3>
        <input value={name} onChange={e => setName(e.target.value)} maxLength={60} autoFocus aria-label="Name for this number" style={S.nameInput}
          placeholder="Name, e.g. the company or department"
          onKeyDown={e => { if (e.key === 'Enter' && !busy) save(); }} />
        {/* 3.68.8: clients aren't told about sharing (owner's 3.66.0 choice); Super Admins are */}
        <p style={S.nameHint}>
          {superView
            ? <>Saved as <strong>{clientName}</strong>'s name. A company name is also shared with every client that hasn't named this number, unless Fax Portal support set it.</>
            : 'Everyone at your company sees this name in Outbox, Inbox and Contacts.'}
          {k?.listing ? <><br />{emptyLine(k.listing)}</> : null}
        </p>
        <div style={S.nameBtns}>
          <button type="button" onClick={onClose} style={S.nameCancel}>Cancel</button>
          <button type="button" onClick={save} disabled={busy} style={S.nameSave}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

// The list row carries the freshest live stage; the history record may be
// older, so prefer the row's progress fields when showing the same fax
function rowLive(row, fax) {
  return row.faxId === fax.faxId
    ? { stage: row.stage, stageAt: row.stageAt, status: row.status }
    : {};
}

function Preview({ preview, fileName, onDownload, short = false }) {
  if (preview.state === 'none') {
    return (
      <div style={S.previewEmpty}>
        <div style={S.previewIcon}><Emo e="📠" /></div>
        <div style={S.previewMsg}>No document</div>
        <div style={S.muted}>This fax failed before it was received, so there is nothing to preview.</div>
      </div>
    );
  }
  if (preview.state === 'loading') {
    return (
      <div style={S.previewEmpty}>
        <Loader size="sm" label="Loading preview…" />
      </div>
    );
  }
  if (preview.state === 'error') {
    return (
      <div style={S.previewEmpty}>
        <div style={S.previewIcon}><Emo e="⚠️" /></div>
        <div style={S.previewMsg}>Preview unavailable</div>
        <div style={S.muted}>{preview.error}</div>
      </div>
    );
  }
  if (preview.mime === 'application/pdf') {
    // 3.54.2 (owner's phone screenshot): phones can't show a PDF in a frame, so the pages are drawn as pictures
    if (needsPdfPages()) return <PdfPages blob={preview.blob} fileName={fileName} />;
    // Hide the viewer's thumbnail sidebar and fit the page to the width
    return <iframe src={`${preview.url}#navpanes=0&view=FitH`} title={`Preview of ${fileName}`} style={short ? { ...S.previewFrame, height: '43.5vh', minHeight: 240 } : S.previewFrame} />;
  }
  if (preview.mime.startsWith('image/')) {
    return (
      <div style={(short ? { ...S.previewImageWrap, maxHeight: '43.5vh' } : S.previewImageWrap)}>
        <img src={preview.url} alt={`Preview of ${fileName}`} style={S.previewImage} />
      </div>
    );
  }
  return (
    <div style={S.previewEmpty}>
      <div style={S.previewIcon}><Emo e="📄" /></div>
      <div style={S.previewMsg}>No preview for this file type</div>
      <div style={S.muted}>Word documents can't be shown in the browser.</div>
      <button onClick={onDownload} style={{ ...S.secondaryBtn, marginTop: 12 }}>Download to view</button>
    </div>
  );
}

function Timeline({ chain, current, onSelect, scheduledRetry, waitingLine }) {
  const tm = useSendTimings();   // 3.75.0
  const last = chain[chain.length - 1];
  return (
    <ol style={S.timeline}>
      {chain.map((a, i) => {
        const label = i === 0 ? 'Original send'
          : a.autoRetry ? `Automatic retry${a.attempt ? ` (attempt ${a.tryNo || a.attempt})` : ''}`
          : `Manual resend${a.tryNo ? ` (attempt ${a.tryNo})` : ''}`;
        const isCurrent = a.faxId === current;
        const outcome = a.status === 'delivered' ? `Delivered ${formatDate(a.deliveredAt)}`
          : a.status === 'failed' ? `Failed ${formatDate(a.updatedAt)}`
          : 'Waiting for delivery';
        return (
          <li key={a.faxId} style={S.tlItem}>
            <span style={{ ...S.tlDot, background: (STATUS_COLORS[a.status] || {}).color || 'var(--c-90a4ae)' }} />
            <button onClick={() => onSelect(a.faxId)} disabled={isCurrent}
              style={{ ...S.tlCard, ...(isCurrent ? S.tlCardCurrent : {}) }}
              aria-current={isCurrent ? 'true' : undefined}>
              <div style={S.tlTop}>
                <span style={S.tlLabel}>{label}</span>
                <Badge status={a.status} small />
              </div>
              <div style={S.tlLine}>Sent {formatDate(a.sentAt)}</div>
              <div style={S.tlLine}>{outcome}</div>
              {a.status === 'failed' && a.failureReason && (
                <div style={{ ...S.tlLine, color: 'var(--t-b71c1c)' }}>{reasonText(a.failureReason, a.failureContext)}</div>
              )}
              {a.requeuedBy === 'superseded-by-manual-send' && <div style={{ ...S.tlLine, color: 'var(--t-6a1b9a)' }}>Automatic retry not needed: user resent</div>}
              {!isCurrent && <div style={S.tlHint}>View this attempt</div>}
            </button>
          </li>
        );
      })}
      {scheduledRetry && (
        <li style={S.tlItem}>
          <span style={{ ...S.tlDot, background: 'var(--c-e65100)' }} />
          <div style={{ ...S.tlCard, cursor: 'default', borderStyle: 'dashed' }}>
            <div style={S.tlLabel}>Automatic retry scheduled</div>
            <div style={S.tlLine}>
              {scheduledRetry.attempt ? `Attempt ${last.tryNo ? last.tryNo + 1 : scheduledRetry.attempt} · ` : ''}around {formatRetryWindow(scheduledRetry.fireAt)}
            </div>
          </div>
        </li>
      )}
      {/* 3.69.4 (owner): resent, waiting for the line (one call at a time) */}
      {waitingLine && (
        <li style={S.tlItem}>
          <span style={{ ...S.tlDot, background: 'var(--c-e65100)' }} />
          <div style={{ ...S.tlCard, cursor: 'default', borderStyle: 'dashed' }}>
            <div style={S.tlLabel}>Resent · waiting for the line</div>
            <div style={S.tlLine}>
              Resent{waitingLine.resentBy ? ` by ${waitingLine.resentBy}` : ''}{waitingLine.resentAt ? ` ${formatDate(waitingLine.resentAt)}` : ''}
            </div>
            <div style={S.tlLine}>
              {waitingLine.position ? `${waitingLine.position} of ${waitingLine.of} in line · ` : ''}goes out in {Math.max(1, Math.ceil(waitingLine.etaMin || 0))} min or less
              {waitingLine.hours ? `, once office hours start (${waitingLine.hours})` : ''}
            </div>
            {waitingLine.slow && <div style={S.tlLine}>This number is hard to reach, so faxes to it go out {tm.hardSpacing} apart.</div>}
          </div>
        </li>
      )}
      {!scheduledRetry && !waitingLine && last?.retryCancelled && (
        <li style={S.tlItem}>
          <span style={{ ...S.tlDot, background: 'var(--c-90a4ae)' }} />
          <div style={{ ...S.tlCard, cursor: 'default' }}>
            <div style={S.tlLabel}>Automatic retry cancelled</div>
            <div style={S.tlLine}>
              {RETRY_CANCEL_REASONS[last.retryCancelled]
                ? `It was cancelled because ${RETRY_CANCEL_REASONS[last.retryCancelled]}.`
                : 'It was cancelled.'}
            </div>
          </div>
        </li>
      )}
    </ol>
  );
}

function Field({ label, value, mono }) {
  return (
    <div style={S.field}>
      <dt style={S.dt}>{label}</dt>
      <dd style={{ ...S.dd, ...(mono ? { fontFamily: 'monospace' } : {}) }}>{value || '—'}</dd>
    </div>
  );
}

function Badge({ status, small }) {
  const s = STATUS_COLORS[status] || { bg: 'var(--c-f5f5f5)', color: 'var(--t-666666)' };
  return (
    <span style={{
      background: s.bg, color: s.color, padding: small ? '1px 8px' : '3px 10px',
      borderRadius: 20, fontSize: small ? 10 : 12, fontWeight: 700,
      textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap'
    }}>
      {status || 'unknown'}
    </span>
  );
}

const S = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgb(var(--c-0f2823-rgb) / 0.35)',
    zIndex: 1000, display: 'flex', justifyContent: 'flex-end'
  },
  panel: {
    width: 'min(600px, 100vw)', height: '100%', background: 'var(--c-ffffff)',
    display: 'flex', flexDirection: 'column',
    boxShadow: '-12px 0 40px rgb(var(--c-000000-rgb) / 0.18)', animation: 'slideIn 0.18s ease-out'
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12,
    padding: '18px 20px', borderBottom: '2px solid var(--c-e0f2f1)', background: 'var(--c-f9fafb)'
  },
  eyebrow: { fontSize: 11, fontWeight: 700, color: 'var(--t-78909c)', textTransform: 'uppercase', letterSpacing: '0.6px' },
  title: { margin: '2px 0 0', fontSize: 19, fontWeight: 700, color: 'var(--t-00695c)', fontFamily: 'inherit' },
  headerBtns: { display: 'flex', gap: 6, flexShrink: 0 },
  // 3.68.7: the number names it; the name sits under the number
  numberBtn: { background: 'none', border: 'none', padding: 0, margin: 0, font: 'inherit', color: 'inherit', cursor: 'pointer', borderBottom: '1.5px dashed var(--c-80cbc4)' },
  pencil: { fontSize: 13, color: 'var(--t-78909c)', fontWeight: 400 },
  numberName: { fontSize: 13.5, fontWeight: 700, color: 'var(--t-00897b)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 380 },
  addName: { background: 'none', border: 'none', padding: 0, marginTop: 3, fontSize: 12.5, fontWeight: 600, color: 'var(--t-00897b)', cursor: 'pointer', fontFamily: 'inherit' },
  nameOverlay: { position: 'fixed', inset: 0, zIndex: 1100, background: 'rgb(var(--c-000000-rgb) / 0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  nameCard: { width: 'min(420px, 100%)', background: 'var(--c-ffffff)', borderRadius: 14, padding: '18px 20px', boxShadow: '0 16px 40px rgb(var(--c-000000-rgb) / 0.22)' },
  nameTitle: { margin: '0 0 12px', fontSize: 16, fontWeight: 700, color: 'var(--t-00695c)' },
  nameInput: { width: '100%', boxSizing: 'border-box', padding: '9px 11px', borderRadius: 8, border: '1.5px solid var(--c-cfd8dc)', fontSize: 14, fontFamily: 'inherit' },
  nameHint: { margin: '8px 0 0', fontSize: 12.5, color: 'var(--t-78909c)', lineHeight: 1.45 },
  nameBtns: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 },
  nameCancel: { padding: '7px 14px', borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', background: 'var(--c-ffffff)', color: 'var(--t-546e7a)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  nameSave: { padding: '7px 16px', borderRadius: 8, border: 'none', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  navBtn: {
    width: 34, height: 34, borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', background: 'var(--c-ffffff)',
    cursor: 'pointer', fontSize: 20, lineHeight: 1, color: 'var(--t-546e7a)', fontFamily: 'inherit'
  },
  closeBtn: {
    width: 34, height: 34, borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', background: 'var(--c-ffffff)',
    cursor: 'pointer', fontSize: 15, color: 'var(--t-546e7a)', fontFamily: 'inherit'
  },
  body: { flex: 1, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 18 },
  failBox: {
    background: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)', borderRadius: 10, padding: '12px 14px',
    fontSize: 14, lineHeight: 1.5
  },
  failLabel: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 },
  previewFrame: {
    width: '100%', height: '58vh', minHeight: 320, border: '1px solid var(--c-e0e0e0)',
    borderRadius: 10, background: 'var(--c-f4f7f6)', flexShrink: 0
  },
  previewImageWrap: {
    border: '1px solid var(--c-e0e0e0)', borderRadius: 10, background: 'var(--c-f4f7f6)',
    maxHeight: '58vh', overflow: 'auto', flexShrink: 0
  },
  previewImage: { display: 'block', width: '100%' },
  previewEmpty: {
    border: '1.5px dashed var(--c-cfd8dc)', borderRadius: 10, background: 'var(--c-fafafa)',
    padding: '36px 20px', textAlign: 'center', display: 'flex', flexDirection: 'column',
    alignItems: 'center', gap: 4, flexShrink: 0
  },
  previewIcon: { fontSize: 30 },
  previewMsg: { fontSize: 15, fontWeight: 600, color: 'var(--t-37474f)' },
  muted: { fontSize: 13, color: 'var(--t-607d8b)', lineHeight: 1.5 },
  details: {
    margin: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: '12px 20px'
  },
  field: { minWidth: 0 },
  dt: { fontSize: 11, fontWeight: 700, color: 'var(--t-607d8b)', textTransform: 'uppercase', letterSpacing: '0.5px' },
  dd: { margin: '3px 0 0', fontSize: 14, color: 'var(--t-263238)', overflowWrap: 'anywhere' },
  refId: { fontFamily: 'monospace', fontSize: 12, color: 'var(--t-37474f)', overflowWrap: 'anywhere' },
  copyBtn: {
    flexShrink: 0, padding: '3px 10px', borderRadius: 6, border: '1.5px solid var(--c-b2dfdb)',
    background: 'var(--c-ffffff)', color: 'var(--t-00695c)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit'
  },
  sectionTitle: {
    margin: '0 0 10px', fontSize: 12, fontWeight: 700, color: 'var(--t-607d8b)',
    textTransform: 'uppercase', letterSpacing: '0.5px'
  },
  timeline: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 },
  tlItem: { display: 'flex', gap: 10, alignItems: 'flex-start' },
  tlDot: { width: 10, height: 10, borderRadius: '50%', marginTop: 14, flexShrink: 0 },
  tlCard: {
    flex: 1, textAlign: 'left', background: 'var(--c-ffffff)', border: '1.5px solid var(--c-e0e0e0)',
    borderRadius: 10, padding: '10px 12px', cursor: 'pointer', fontFamily: 'inherit', color: 'inherit'
  },
  tlCardCurrent: { border: '1.5px solid var(--c-00897b)', background: 'var(--c-f0faf8)', cursor: 'default', opacity: 1 },
  tlTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 4 },
  tlLabel: { fontSize: 14, fontWeight: 600, color: 'var(--t-263238)' },
  tlLine: { fontSize: 12, color: 'var(--t-546e7a)', lineHeight: 1.6 },
  tlHint: { fontSize: 11, color: 'var(--t-00897b)', fontWeight: 600, marginTop: 4 },
  footer: {
    display: 'flex', gap: 10, flexWrap: 'wrap', padding: '14px 20px',
    borderTop: '1px solid var(--c-eceff1)', background: 'var(--c-f9fafb)'
  },
  primaryBtn: {
    padding: '10px 18px', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 8,
    cursor: 'pointer', fontSize: 14, fontWeight: 600, fontFamily: 'inherit'
  },
  btnDisabled: { opacity: 0.45, cursor: 'not-allowed' },
  secondaryBtn: {
    padding: '10px 16px', background: 'var(--c-ffffff)', color: 'var(--t-37474f)', border: '1.5px solid var(--c-cfd8dc)',
    borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, fontFamily: 'inherit'
  },
  deleteBtn: {
    marginLeft: 'auto', padding: '9px 16px', background: 'var(--c-ffffff)', color: 'var(--t-b71c1c)', border: '1.5px solid var(--c-ef9a9a)',
    borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, fontFamily: 'inherit'
  },
  cancelRetryBtn: {
    padding: '10px 16px', background: 'var(--c-ffffff)', color: 'var(--t-b71c1c)', border: '1.5px solid var(--c-ef9a9a)',
    borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, fontFamily: 'inherit', marginLeft: 'auto'
  },
  actionError: { width: '100%', fontSize: 13, color: 'var(--t-b71c1c)' },
  resendBtn: {
    padding: '10px 16px', background: 'var(--c-fff8e1)', color: 'var(--t-e65100)', border: '1.5px solid var(--c-f9a825)',
    borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, fontFamily: 'inherit', marginLeft: 'auto'
  },
};
